/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { HiOutlinePhotograph } from 'react-icons/hi'
import placeholder from '../../assets/placeholder.jpeg'
import {
  ImageWrapper,
  ImageLazy,
  ImagePlaceholder,
  ImageDefault
} from './Image.styles'
import { ImageProps } from './Image.types'
import { checkIfImageExists, createResizeUrl } from './Image.utils'
import { usePrevious } from '../../hooks/usePrevious'

const Image = ({
  className = '',
  src,
  styles,
  placeholderImage,
  resizeEnabled = false,
  resizeServiceUrl,
  allowLazyLoading = true,
  dimensions,
  ...props
}: ImageProps) => {
  const [elemWidth, setElemWidth] = useState(0)
  const [hasError, setHasError] = useState(false)
  const [imageUrl, setImageUrl] = useState(
    createResizeUrl(src, resizeEnabled, dimensions, resizeServiceUrl)
  )

  useEffect(() => {
    const imageSrc: string = createResizeUrl(
      src,
      resizeEnabled,
      dimensions,
      resizeServiceUrl
    )
    setImageUrl(imageSrc)
    if (resizeEnabled && src) {
      checkIfImageExists(imageSrc || imageUrl, setImageUrl, src)
    }
  }, [src, resizeEnabled, dimensions, resizeServiceUrl])

  const imgRef = useRef<HTMLDivElement>()

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return
        }
        const newWidth = entries[0].contentRect.width
        setElemWidth(newWidth)
      })
    })

    if (imgRef.current) {
      resizeObserver.observe(imgRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const previousSRC = usePrevious(src)

  useEffect(() => {
    if (previousSRC !== src) {
      // when item card modal is reused, it will reset the error state
      setHasError(false)
    }
  }, [previousSRC, src])

  return (
    <ImageWrapper
      className={`Image ${className}`}
      sx={styles}
      ref={imgRef}
      elemWidth={elemWidth}
      {...props}>
      {allowLazyLoading
        ? src && (
            <ImageLazy
              className="ImageLazy"
              src={imageUrl}
              onError={({ currentTarget }: any) => {
                currentTarget.onerror = null // prevents looping
                if (placeholderImage && !hasError) {
                  currentTarget.src = placeholderImage
                  setHasError(true)
                }
              }}
            />
          )
        : src && (
            <ImageDefault
              src={src}
              hasError={hasError}
              onError={({ currentTarget }: any) => {
                currentTarget.onerror = null // prevents looping
                if (!hasError) {
                  setHasError(true)
                }
              }}
              alt=""
            />
          )}
      <ImagePlaceholder className="Placeholder">
        {placeholderImage && hasError && (
          <img
            src={placeholderImage}
            onError={({ currentTarget }: any) => {
              currentTarget.onerror = null // prevents looping
              currentTarget.src = placeholder
            }}
            alt=""
          />
        )}
        {!placeholderImage && <HiOutlinePhotograph />}
      </ImagePlaceholder>
    </ImageWrapper>
  )
}

export default Image
